import React, { ReactNode, FunctionComponent } from 'react';
import classNames from 'classnames';
import DangerHtml from 'components/danger-html/danger-html';

export enum TextColor {
    PRIMARY = 1,
    PRIMARY_LIGHT,
    GENERIC_RED,
    GENERIC_PINK,
    GENERIC_YELLOW,
    GENERIC_GREEN,
    GENERIC_PURPLE,
    GENERIC_BLUE,
    BLACK,
    GRAY_DARK,
    GRAY,
    GRAY_LIGHT,
    WHITE,
    ERROR,
}

const mapTextColorToClassName = {
    [TextColor.PRIMARY]: 'text-color-primary',
    [TextColor.PRIMARY_LIGHT]: 'text-color-primary-light',
    [TextColor.GENERIC_RED]: 'text-color-generic-red',
    [TextColor.GENERIC_PINK]: 'text-color-generic-pink',
    [TextColor.GENERIC_YELLOW]: 'text-color-generic-yellow',
    [TextColor.GENERIC_GREEN]: 'text-color-generic-green',
    [TextColor.GENERIC_PURPLE]: 'text-color-generic-purple',
    [TextColor.GENERIC_BLUE]: 'text-color-generic-blue',
    [TextColor.BLACK]: 'text-color-black',
    [TextColor.GRAY_DARK]: 'text-color-gray-dark',
    [TextColor.GRAY]: 'text-color-gray',
    [TextColor.GRAY_LIGHT]: 'text-color-gray-light',
    [TextColor.WHITE]: 'text-color-white',
    [TextColor.ERROR]: 'text-color-error',
};

export enum TextSize {
    HEADER1 = 1,
    HEADER2,
    HEADER3,
    ACTION,
    P1,
    P2,
    P3,
}

const mapTextSizeToClassName = {
    [TextSize.HEADER1]: 'header-1',
    [TextSize.HEADER2]: 'header-2',
    [TextSize.HEADER3]: 'header-3',
    [TextSize.ACTION]: 'action',
    [TextSize.P1]: 'p-1',
    [TextSize.P2]: 'p-2',
    [TextSize.P3]: 'p-3',
};

export enum TextWeight {
    BOOK = 1,
    MEDIUM,
}

const mapTextWeightToClassName = {
    [TextWeight.BOOK]: 'font-weight-book',
    [TextWeight.MEDIUM]: 'font-weight-medium',
};

export enum TextAlign {
    LEFT = 1,
    CENTER,
    RIGHT,
    INHERITED,
}

const mapTextAlignToClassName = {
    [TextAlign.LEFT]: 'text-align-left',
    [TextAlign.CENTER]: 'text-align-center',
    [TextAlign.RIGHT]: 'text-align-right',
    [TextAlign.INHERITED]: 'text-align-inherited',
};

type TextProps = {
    size: TextSize;
};

export enum TextTag {
    H1 = 1,
    H2,
    H3,
    H4,
    H5,
    H6,
    P,
    SPAN,
}

type TextMapProps = {
    classNames: string;
    content: ReactNode;
};

const mapTagToText: Record<TextTag, FunctionComponent<TextMapProps>> = {
    // eslint-disable-next-line react/no-multi-comp
    [TextTag.H1]: (props) => <h1 className={props.classNames}>{props.content}</h1>,
    // eslint-disable-next-line react/no-multi-comp
    [TextTag.H2]: (props) => <h2 className={props.classNames}>{props.content}</h2>,
    // eslint-disable-next-line react/no-multi-comp
    [TextTag.H3]: (props) => <h3 className={props.classNames}>{props.content}</h3>,
    // eslint-disable-next-line react/no-multi-comp
    [TextTag.H4]: (props) => <h4 className={props.classNames}>{props.content}</h4>,
    // eslint-disable-next-line react/no-multi-comp
    [TextTag.H5]: (props) => <h5 className={props.classNames}>{props.content}</h5>,
    // eslint-disable-next-line react/no-multi-comp
    [TextTag.H6]: (props) => <h6 className={props.classNames}>{props.content}</h6>,
    // eslint-disable-next-line react/no-multi-comp
    [TextTag.P]: (props) => <p className={props.classNames}>{props.content}</p>,
    // eslint-disable-next-line react/no-multi-comp
    [TextTag.SPAN]: (props) => <span className={props.classNames}>{props.content}</span>,
};

export type TextBasicProps = {
    /**
     * If the string/node needs to be rendered as HTML
     * @notice It's set via the dangerouslySetInnerHTML React API
     */
    isDangerous?: boolean;
    /**
     * A string, a node, an array of nodes to be rendered. If "dangerous" is set to true, then it must be strictly a string.
     * @param {Object} props Children props
     * @custom node|string
     */
    children: ReactNode | string;
    tag?: TextTag;
};

export type TextCommonProps = {
    /**
     * Defines how inline-level content of a block container is aligned
     */
    align?: TextAlign;
    /**
     * Defines color of the text
     */
    color?: TextColor;
    /**
     * The "display" of the text
     * @notice This controls the HTML element used to contain the text node: a <span> if "inline" is true, otherwise by default a <div>
     */
    isInline?: boolean;
    /**
     * If the text needs to be trimmed with an ellipsis
     */
    hasEllipsis?: boolean;
    /**
     * If the long words present in the text and should be broken into lines not to break the layout
     */
    hasBreakWords?: boolean;
};

export type TextExtendedProps = {
    /**
     * The weight of the font
     */
    weight?: TextWeight;
};

type TextComponentProps = TextProps & TextBasicProps & TextCommonProps & TextExtendedProps;

// eslint-disable-next-line react/no-multi-comp
const Text: FunctionComponent<TextComponentProps> = (props) => {
    const {
        size,
        weight,
        align,
        color,
        isInline,
        hasEllipsis,
        hasBreakWords,
        isDangerous,
        children,
        tag,
    } = props;

    const className = classNames(
        {
            [mapTextSizeToClassName[size]]: true,
            'text-ellipsis': hasEllipsis,
            'text-break-words': hasBreakWords,
        },
        typeof weight !== 'undefined' && mapTextWeightToClassName[weight],
        typeof align !== 'undefined' && mapTextAlignToClassName[align],
        typeof color !== 'undefined' && mapTextColorToClassName[color]
    );

    const content =
        isDangerous && typeof children === 'string' ? <DangerHtml html={children} /> : children;

    if (isInline && !hasEllipsis) {
        return <span className={className}>{content}</span>;
    }

    if (tag) {
        const Component = mapTagToText[tag];

        return <Component classNames={className} content={content} />;
    }

    return <div className={className}>{content}</div>;
};

export default Text;
